import React, { useEffect, useRef, useState } from 'react';
import { hasOwnProperty } from '@go-shared/lib/utils';
import { DefaultedText } from '../DefaultedText';
import BubbleInput from './BubbleInput';
import BubbleButton from './BubbleButton';

interface BubbleHintSelectPropsBase<T> {
  value: T;
  options: T[];
  block?: boolean;
  readOnly?: boolean;
  menuClassName?: string;
  keyExtractor?: (value: T) => string;
  labelExtractor?: (value: T) => string;
  internalValueExtractor?: (value: T) => string;
  onValue?: (value: T) => void;
}

export type BubbleHintSelectProps<T> = React.HTMLAttributes<HTMLDivElement> &
  BubbleHintSelectPropsBase<T>;

export default function BubbleHintSelect<T extends string | number | object>(
  props: BubbleHintSelectProps<T>
) {
  const {
    // from component
    value,
    options,
    block,
    keyExtractor,
    labelExtractor,
    internalValueExtractor,
    readOnly,
    onValue,
    menuClassName,
    // from element
    className: propClassName,
    style: propStyle,
    ...rest
  } = props;

  const classes = ['bubble-hintselect-container'];
  if (block) classes.push('full');
  if (propClassName) classes.push(propClassName);

  let style: React.CSSProperties = {};
  if (propStyle) style = { ...style, ...propStyle };

  const menuClasses = ['bubble-hintselect-menu bg-white py-2 shadow rounded-br rounded-bl'];
  if (menuClassName) menuClasses.push(menuClassName);

  const internalValue = resolveInternalValue(value);
  const currentOption = options.find(o => resolveInternalValue(o) === internalValue);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const [search, setSearch] = useState('');
  const [inputDirty, setInputDirty] = useState(false);
  const [inputHasFocus, setInputHasFocus] = useState(false);

  useEffect(() => {
    calculateFocusStates();
  }, [value]);

  function resolveInternalValue(val: T) {
    if (internalValueExtractor) return internalValueExtractor(val);
    else if (typeof val === 'object') return JSON.stringify(val);
    else return `${val}`;
  }

  function resolveLabel(val: T) {
    if (labelExtractor) return labelExtractor(val);
    else return resolveInternalValue(val);
  }

  function resolveKey(val: T) {
    if (keyExtractor) {
      return keyExtractor(val);
    } else if (hasOwnProperty(val, 'key') && ['string', 'number'].includes(typeof val.key)) {
      return `${val.key}`;
    } else {
      return resolveInternalValue(val);
    }
  }

  function onSelectItem(e: React.MouseEvent<HTMLButtonElement>, val: T) {
    e.preventDefault();
    e.stopPropagation();

    if (onValue) onValue(val);
    inputRef.current?.blur();
  }

  function calculateFocusStates() {
    if (value) {
      setSearch(resolveLabel(value));
      setInputDirty(false);
    }
    setInputHasFocus(inputRef.current ? document.activeElement === inputRef.current : false);
  }

  function onInputHandler(text: string) {
    setSearch(text);
    setInputDirty(true);
  }

  const filteredOptions = !inputDirty
    ? options
    : options.filter(o => resolveLabel(o).toLowerCase().includes(search.toLowerCase()));

  return (
    <div className={classes.join(' ')} style={style}>
      {readOnly ? (
        <DefaultedText
          val={currentOption ? `${resolveLabel(currentOption)}` : undefined}
          def="None"
        />
      ) : (
        <div {...rest}>
          <BubbleInput
            inputRef={inputRef}
            type="text"
            block
            inputClassName={inputDirty ? 'text-crimson' : ''}
            value={search}
            onValue={onInputHandler}
            onFocus={calculateFocusStates}
            onBlur={calculateFocusStates}
          />
          {inputHasFocus && (
            <div className={menuClasses.join(' ')} onMouseDown={e => e.preventDefault()}>
              {filteredOptions.map(o => (
                <BubbleButton
                  className="text-left"
                  block
                  variant="navy"
                  key={resolveKey(o)}
                  inverse
                  onMouseDown={e => onSelectItem(e, o)}
                >
                  {resolveLabel(o)}
                </BubbleButton>
              ))}
              {filteredOptions.length < 1 && (
                <div className="px-4 py-2 italic text-smoke">No results found</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
